/** Sign-in.
 *
 * Refuses to render while a session already exists.
 *
 * Reported 2026-10-01: an admin signed in as a partner opened an
 * operator's invitation link in the same browser, hit "sign in", and
 * logged in as the operator — silently replacing the session she was
 * already working in. One browser holds ONE token (see tokenStore), so
 * the second sign-in does not sit alongside the first, it overwrites it.
 *
 * The cheapest honest fix is to not offer the form at all when a token
 * is already there: landing on /login while signed in sends you where
 * that session belongs. Signing out is the way to switch accounts, and
 * it clears the token, so /login works again immediately afterwards —
 * the escape hatch is the one people already use.
 *
 * `?next=` is preserved so a deep link that bounced through /login
 * still arrives at its destination instead of the dashboard.
 */
import { Navigate, useSearchParams } from "react-router-dom";

import { BrandLogo } from "@/components/BrandLogo";
import { isAuthenticated } from "@/lib/auth";
import { safeNextPath } from "@/lib/safeNext";
import { LoginForm } from "@/modules/auth";

export default function LoginPage() {
  const [params] = useSearchParams();

  if (isAuthenticated()) {
    // "/" rather than "/dashboard": RootRedirect already knows where a
    // support-staff or partner session belongs, and hardcoding the
    // customer dashboard here would send them to the wrong place.
    return <Navigate to={safeNextPath(params.get("next")) ?? "/"} replace />;
  }

  return (
    <main className="min-h-dvh bg-nb-bg py-10">
      <div className="flex justify-center">
        <BrandLogo />
      </div>
      <LoginForm />
    </main>
  );
}
